// =============================================================================
// RoutineBuilder — GUI block builder for attack routines (Phase 3)
//
// Attack modifiers and damage modifiers are now composable lists of components:
//   Stat Ref  — resolved against the live character sheet at roll time
//   Fixed Bonus — a static integer with a descriptive label
//
// Example attack: d20 + BAB Max (20) + STR (5) + 1 (Weapon Focus) - 2 (Rapid Shot) = +24
// Example damage: 2d6 + STR (5) + 4 (Enhancement) = [roll]+9
// =============================================================================
import { useState } from 'react';
import { Plus, Trash2, Copy, ChevronDown, ChevronUp, Check, X, Wand2 } from 'lucide-react';
import type { AttackRoutine, AttackNode, AttackKind, DamageNode, BonusAttackNode, SpecialDamageType, WeaponSpecialProperty } from '../../../../backend/shared/types/index';
import type { RuleSystem } from '../../../../backend/shared/types/index';
import { SPECIAL_DAMAGE_LABELS } from '../../../../backend/shared/types/index';
import { isPureDiceExpr, parseDiceExpr } from '@/engine/diceParser';
import {
  defaultStatKeyForSystem,
  normalizeStatKeyForSystem,
  statsForSystem,
  STAT_DEF_MAP,
} from '@/engine/statResolver';
import { parseRSLScript } from '@/engine/rslParser';
import { WEAPON_SIZES, type WeaponSize } from '@/engine/sizeProgression';
import ScriptEditor from './ScriptEditor';

// ---------------------------------------------------------------------------
// Draft types (component-local, not stored in DB directly)
// ---------------------------------------------------------------------------
interface ModCompDraft {
  id:         string;
  type:       'stat_ref' | 'manual';
  statKey:    string;
  value:      number;
  descriptor: string;
}

interface DamageDraft {
  id:            string;
  diceExpr:      string;         // dice notation ("2d6") OR plain integer ("2") for fixed-value damage
  modComponents: ModCompDraft[]; // modifier components summed at roll time
  notes:         string;
  critMult:      number;
  weaponSize?:   WeaponSize;       // if set, participates in pre-roll size adjustment
  specialType?:  SpecialDamageType; // if set, excluded from HP totalDamage
}

interface AttackNodeDraft {
  id:               string;
  label:            string;
  attackKind?:      AttackKind;
  attackComponents: ModCompDraft[];
  vsTarget:         string;
  critMin:          number;
  noCrit:           boolean;
  critStyle?:       'standard' | 'perfect';  // 5e only
  weaponProperties: WeaponSpecialProperty[];
  damage:           DamageDraft[];
  onCritExtra:      DamageDraft[];
  expanded:         boolean;
}

interface BonusAttackNodeDraft {
  id:        string;
  condition: {
    subject: 'hits' | 'crit_hits';
    op:      'gte' | 'lte' | 'eq';
    value:   number;
  };
  attack:    AttackNodeDraft;
  expanded:  boolean;
}

function newStatRef(key = 'bab'): ModCompDraft {
  return { id: crypto.randomUUID(), type: 'stat_ref', statKey: key, value: 0, descriptor: '' };
}
function newManual(): ModCompDraft {
  return { id: crypto.randomUUID(), type: 'manual', statKey: '', value: 0, descriptor: '' };
}
function blankDamage(): DamageDraft {
  return { id: crypto.randomUUID(), diceExpr: '', modComponents: [], notes: '', critMult: 2 };
}
function blankAttack(): AttackNodeDraft {
  return {
    id: crypto.randomUUID(), label: 'Attack',
    attackKind: 'melee',
    attackComponents: [],
    vsTarget: 'AC', critMin: 20, noCrit: false, critStyle: undefined,
    weaponProperties: [],
    damage: [blankDamage()], onCritExtra: [], expanded: true,
  };
}

function blankBonusAttack(): BonusAttackNodeDraft {
  return {
    id: crypto.randomUUID(),
    condition: { subject: 'hits', op: 'gte', value: 1 },
    attack: blankAttack(),
    expanded: true,
  };
}

/** Convert ModCompDraft → ModifierComponent (stored shape). */
function draftCompToStored(c: ModCompDraft, gameSystem: RuleSystem) {
  return {
    id:         c.id,
    type:       c.type,
    statKey:    c.type === 'stat_ref'
      ? normalizeStatKeyForSystem(c.statKey || defaultStatKeyForSystem(gameSystem), gameSystem)
      : undefined,
    value:      c.type === 'manual'   ? c.value   : undefined,
    descriptor: c.descriptor || undefined,
  };
}

/** Convert DamageDraft → DamageNode. */
function draftDamageToNode(dd: DamageDraft, gameSystem: RuleSystem): DamageNode {
  return {
    id:            dd.id,
    diceExpr:      dd.diceExpr,
    modComponents: dd.modComponents.map((c) => draftCompToStored(c, gameSystem)),
    notes:         dd.notes,
    critMult:      dd.critMult,
    weaponSize:    dd.weaponSize,
    specialType:   dd.specialType,
  };
}

function draftToNode(d: AttackNodeDraft, gameSystem: RuleSystem): AttackNode {
  // Keep a numeric attackMod as a fallback for display (sum manual components only).
  const manualSum = d.attackComponents
    .filter((c) => c.type === 'manual')
    .reduce((s, c) => s + (c.value || 0), 0);
  return {
    type:             'attack',
    id:               d.id,
    label:            d.label,
    attackKind:       d.attackKind,
    attackMod:        manualSum,
    attackComponents: d.attackComponents.map((c) => draftCompToStored(c, gameSystem)),
    vsTarget:         d.vsTarget,
    critRange:        [d.critMin, 20],
    noCrit:          d.noCrit || undefined,
    critStyle:       d.critStyle || undefined,
    weaponProperties: d.weaponProperties.length ? d.weaponProperties : undefined,
    damage:           d.damage.map((dd) => draftDamageToNode(dd, gameSystem)),
    onCritExtra:      d.onCritExtra.map((dd) => ({ ...draftDamageToNode(dd, gameSystem), critMult: 1 })),
  };
}

/**
 * Migrate stored DamageNode → DamageDraft for the builder form.
 *
 * Handles two legacy cases:
 *   "2d6+15" → diceExpr="2d6", modComponent value=15  (embedded mod stripped)
 *   "2"      → diceExpr="2", modComponents=[]          (flat constant, kept as-is)
 *
 * Only dice expressions (diceCount > 0) can have embedded modifiers to strip.
 * A plain integer diceExpr is a self-contained fixed value — never treat its
 * numeric value as a legacy modifier to move into modComponents.
 */
function migrateDamage(dd: DamageNode): DamageDraft {
  const parsed = parseDiceExpr(dd.diceExpr);
  const hasDice = parsed !== null && parsed.diceCount > 0;

  // Strip embedded modifier only from dice expressions ("2d6+15" → "2d6").
  // Plain integers ("2") stay verbatim — they are fixed-value constants, not modifiers.
  const diceOnly  = hasDice ? `${parsed.diceCount}d${parsed.diceSize}` : dd.diceExpr;
  const legacyMod = hasDice ? (parsed.modifier ?? 0) : 0;

  let modComponents: ModCompDraft[] = [];
  if (dd.modComponents?.length) {
    modComponents = dd.modComponents.map((c) => ({
      id: c.id, type: c.type,
      statKey: c.statKey ?? '', value: c.value ?? 0, descriptor: c.descriptor ?? '',
    }));
  } else if (legacyMod !== 0) {
    modComponents = [{ id: crypto.randomUUID(), type: 'manual', statKey: '', value: legacyMod, descriptor: '' }];
  }
  return { id: dd.id, diceExpr: diceOnly, modComponents, notes: dd.notes ?? '', critMult: dd.critMult ?? 2, weaponSize: dd.weaponSize, specialType: dd.specialType };
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Conditional bonus damage draft type
// ---------------------------------------------------------------------------
interface ConditionalDamageDraft {
  id:        string;
  subject:   'hits' | 'crit_hits';
  op:        'gte' | 'lte' | 'eq';
  value:     number;
  damage:    DamageDraft[];
  label:     string;
}

function blankConditional(): ConditionalDamageDraft {
  return {
    id: crypto.randomUUID(),
    subject: 'hits', op: 'gte', value: 2,
    damage: [blankDamage()], label: '',
  };
}

function conditionalToNode(c: ConditionalDamageDraft, gameSystem: RuleSystem): import('../../../../backend/shared/types/index').ConditionalBonusDamageNode {
  return {
    type: 'conditional_damage',
    id: c.id,
    condition: { subject: c.subject, op: c.op, value: c.value },
    damage: c.damage.map((dd) => draftDamageToNode(dd, gameSystem)),
    label: c.label || undefined,
  };
}

function bonusAttackToNode(ba: BonusAttackNodeDraft, gameSystem: RuleSystem): BonusAttackNode {
  return {
    type: 'bonus_attack',
    id: ba.id,
    condition: { subject: ba.condition.subject, op: ba.condition.op, value: ba.condition.value },
    attack: draftToNode(ba.attack, gameSystem),
    label: ba.attack.label || undefined,  // derived from nested attack name
  };
}

/** Reverse converters: RoutineNode → Draft types (for RSL parsing) */
function storedCompToDraft(c: any, gameSystem?: RuleSystem): ModCompDraft {
  const statKey = c.statKey
    ? normalizeStatKeyForSystem(c.statKey, gameSystem)
    : '';
  return {
    id:         c.id,
    type:       c.type,
    statKey,
    value:      c.value ?? 0,
    descriptor: c.descriptor ?? '',
  };
}

function nodeToDraft(a: AttackNode): AttackNodeDraft {
  return {
    id:               a.id,
    label:            a.label,
    attackKind:       a.attackKind,
    attackComponents: (a.attackComponents ?? []).map((c) => storedCompToDraft(c)),
    vsTarget:         a.vsTarget,
    critMin:          a.critRange?.[0] ?? 20,
    noCrit:           a.noCrit ?? false,
    critStyle:        a.critStyle,
    weaponProperties: a.weaponProperties ?? [],
    damage:           (a.damage ?? []).map((d) => ({
      id:            d.id,
      diceExpr:      d.diceExpr,
      modComponents: (d.modComponents ?? []).map((c) => storedCompToDraft(c)),
      notes:         d.notes ?? '',
      critMult:      d.critMult ?? 2,
      weaponSize:    d.weaponSize,
    })),
    onCritExtra:      (a.onCritExtra ?? []).map((d) => ({
      id:            d.id,
      diceExpr:      d.diceExpr,
      modComponents: (d.modComponents ?? []).map((c) => storedCompToDraft(c)),
      notes:         d.notes ?? '',
      critMult:      d.critMult ?? 1,
      weaponSize:    d.weaponSize,
    })),
    expanded:         false,
  };
}

function nodeToConditional(c: import('../../../../backend/shared/types/index').ConditionalBonusDamageNode): ConditionalDamageDraft {
  return {
    id:      c.id,
    subject: c.condition.subject,
    op:      c.condition.op,
    value:   c.condition.value,
    damage:  (c.damage ?? []).map((d) => ({
      id:            d.id,
      diceExpr:      d.diceExpr,
      modComponents: (d.modComponents ?? []).map((comp) => storedCompToDraft(comp)),
      notes:         d.notes ?? '',
      critMult:      d.critMult ?? 2,
    })),
    label:   c.label ?? '',
  };
}

function nodeToBonus(ba: BonusAttackNode): BonusAttackNodeDraft {
  return {
    id:        ba.id,
    condition: ba.condition,
    attack:    nodeToDraft(ba.attack),
    expanded:  false,
  };
}

// ---------------------------------------------------------------------------
type PresetAttack = Omit<AttackNodeDraft, 'id' | 'expanded'>;

const ATTACK_KINDS: { value: '' | AttackKind; label: string }[] = [
  { value: '',       label: 'Legacy (all types)' },
  { value: 'melee',  label: 'Melee'   },
  { value: 'ranged', label: 'Ranged'  },
  { value: 'spell',  label: 'Spell'   },
];

const PRESETS: Record<string, { label: string; attacks: PresetAttack[] }> = {
  pf1_full_bab20: {
    label: 'PF1 Full Attack (BAB +20)',
    attacks: [
      { label: 'Strike 1', attackKind: 'melee', attackComponents: [{ id: '1', type: 'manual', statKey: '', value: 20, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
      { label: 'Strike 2', attackKind: 'melee', attackComponents: [{ id: '2', type: 'manual', statKey: '', value: 15, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
      { label: 'Strike 3', attackKind: 'melee', attackComponents: [{ id: '3', type: 'manual', statKey: '', value: 10, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
      { label: 'Strike 4', attackKind: 'melee', attackComponents: [{ id: '4', type: 'manual', statKey: '', value:  5, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
    ],
  },
  pf1_full_from_stats: {
    label: 'PF1 Full Attack (from Stats)',
    attacks: [
      { label: 'Strike 1', attackKind: 'melee', attackComponents: [{ id: '1', type: 'stat_ref', statKey: 'bab',     value: 0, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
      { label: 'Strike 2', attackKind: 'melee', attackComponents: [{ id: '2', type: 'stat_ref', statKey: 'bab_2nd', value: 0, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
      { label: 'Strike 3', attackKind: 'melee', attackComponents: [{ id: '3', type: 'stat_ref', statKey: 'bab_3rd', value: 0, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
      { label: 'Strike 4', attackKind: 'melee', attackComponents: [{ id: '4', type: 'stat_ref', statKey: 'bab_4th', value: 0, descriptor: '' }], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
    ],
  },
  single: {
    label: 'Single Attack',
    attacks: [
      { label: 'Attack', attackKind: 'melee', attackComponents: [], vsTarget: 'AC', critMin: 20, noCrit: false, weaponProperties: [], damage: [blankDamage()], onCritExtra: [] },
    ],
  },
};

// ---------------------------------------------------------------------------
// Styling constants
// ---------------------------------------------------------------------------
const VS_TARGETS = ['AC', 'Touch AC', 'Flat-Footed AC', 'CMD', 'Flat-Footed CMD'];
const inputCls   = 'bg-darkbg border border-darkbg-border rounded px-2 py-1 text-parchment text-xs focus:outline-none focus:border-gold w-full placeholder-parchment-muted/25';
const inputErrCls= 'bg-darkbg border border-danger/50 rounded px-2 py-1 text-parchment text-xs focus:outline-none focus:border-danger w-full placeholder-parchment-muted/25';
const selectCls  = 'bg-darkbg border border-darkbg-border rounded px-2 py-1 text-parchment text-xs focus:outline-none focus:border-gold';

// ---------------------------------------------------------------------------
// ModComponentRow — one stat ref or manual bonus in a component list
// ---------------------------------------------------------------------------
function ModComponentRow({ comp, onUpdate, onRemove, gameSystem }: {
  comp:       ModCompDraft;
  onUpdate:   (patch: Partial<ModCompDraft>) => void;
  onRemove:   () => void;
  gameSystem?: RuleSystem;
}) {
  const stats = statsForSystem(gameSystem ?? 'pathfinder1');
  if (comp.type === 'stat_ref') {
    const selectedKey = stats.some((s) => s.key === comp.statKey) ? comp.statKey : '';
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-parchment-muted/40 shrink-0">+</span>
        <select value={selectedKey}
          onChange={(e) => onUpdate({ statKey: e.target.value })}
          className={selectCls + ' flex-1 min-w-0'}>
          {!selectedKey && <option value="" disabled>Select stat</option>}
          {stats.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button onClick={onRemove} title="Remove" className="p-1 text-danger/40 hover:text-danger transition-colors shrink-0">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      {/* Value — labelled so it's clear this is the numeric bonus (+1, -2, etc.)
          Shows blank when 0 so users can type freely without fighting a locked '0'.
          Supports negative values for penalties (e.g. -2 for Rapid Shot). */}
      <div className="flex flex-col shrink-0">
        <span className="text-[9px] text-parchment-muted/30 mb-0.5 text-center">Value</span>
        <input
          type="number"
          value={comp.value === 0 ? '' : comp.value}
          onChange={(e) => {
            const raw = e.target.value;
            const n   = parseInt(raw, 10);
            onUpdate({ value: isNaN(n) ? 0 : n });
          }}
          className={inputCls + ' w-16 text-center'}
          placeholder="±0"
        />
      </div>
      {/* Label — describes what this bonus/penalty is */}
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-[9px] text-parchment-muted/30 mb-0.5">Label</span>
        <input type="text" value={comp.descriptor}
          onChange={(e) => onUpdate({ descriptor: e.target.value })}
          placeholder="e.g. Weapon Focus, Rapid Shot"
          className={inputCls}
          maxLength={50}
        />
      </div>
      <button onClick={onRemove} title="Remove"
        className="p-1 text-danger/40 hover:text-danger transition-colors shrink-0 mt-4">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModComponentsList — editable list with add buttons
// ---------------------------------------------------------------------------
function ModComponentsList({ components, onUpdate, onAdd, onRemove, gameSystem, emptyHint }: {
  components: ModCompDraft[];
  onUpdate:   (idx: number, patch: Partial<ModCompDraft>) => void;
  onAdd:      (type: 'stat_ref' | 'manual') => void;
  onRemove:   (idx: number) => void;
  gameSystem?: RuleSystem;
  emptyHint?: string;
}) {
  return (
    <div className="space-y-1.5">
      {components.length === 0 && emptyHint && (
        <p className="text-[10px] text-parchment-muted/25 italic">{emptyHint}</p>
      )}
      {components.map((c, i) => (
        <ModComponentRow key={c.id} comp={c}
          onUpdate={(p) => onUpdate(i, p)}
          onRemove={() => onRemove(i)}
          gameSystem={gameSystem}
        />
      ))}
      <div className="flex items-center gap-2 pt-0.5">
        <button onClick={() => onAdd('stat_ref')}
          className="text-[10px] text-gold/60 hover:text-gold transition-colors flex items-center gap-0.5">
          <Plus className="w-3 h-3" /> Stat Ref
        </button>
        <button onClick={() => onAdd('manual')}
          className="text-[10px] text-parchment-muted/50 hover:text-parchment transition-colors flex items-center gap-0.5">
          <Plus className="w-3 h-3" /> Fixed Bonus
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DamageRow — dice expression + modifier components + notes/type + crit ×
// ---------------------------------------------------------------------------
function DamageRow({ d, onChange, onRemove, canRemove, gameSystem }: {
  d:          DamageDraft;
  onChange:   (patch: Partial<DamageDraft>) => void;
  onRemove:   () => void;
  canRemove:  boolean;
  gameSystem?: RuleSystem;
}) {
  const diceValid = d.diceExpr === '' || isPureDiceExpr(d.diceExpr);

  function updateMod(idx: number, patch: Partial<ModCompDraft>) {
    onChange({ modComponents: d.modComponents.map((c, i) => i === idx ? { ...c, ...patch } : c) });
  }
  function addMod(type: 'stat_ref' | 'manual') {
    onChange({ modComponents: [...d.modComponents, type === 'stat_ref' ? newStatRef('str_mod') : newManual()] });
  }
  function removeMod(idx: number) {
    onChange({ modComponents: d.modComponents.filter((_, i) => i !== idx) });
  }

  return (
    <div className="bg-darkbg-secondary/30 rounded-lg border border-darkbg-border/50 p-2 space-y-1.5">
      {/* Top row: dice | category | crit × | size | delete */}
      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-1 shrink-0">
          <label className="text-[10px] text-parchment-muted/40 whitespace-nowrap">Damage</label>
          <div className="relative">
            <input value={d.diceExpr} onChange={(e) => onChange({ diceExpr: e.target.value })}
              placeholder="e.g. 2d6, 1d4, or 2"
              className={(diceValid ? inputCls : inputErrCls) + ' w-24'}
              maxLength={10}
            />
          </div>
        </div>
        {/* Special damage category — ability score damage and negative levels */}
        <div className="flex items-center gap-1 shrink-0">
          <label
            className="text-[10px] text-parchment-muted/40 whitespace-nowrap"
            title="HP Damage (default) — or choose Ability Damage / Negative Level to track separately from hit point totals"
          >
            Category
          </label>
          <select
            value={d.specialType ?? ''}
            onChange={(e) => {
              const next = (e.target.value as SpecialDamageType) || undefined;
              // Clearing weaponSize when switching to a special category — size scaling
              // is meaningless for ability damage / negative levels.
              onChange({ specialType: next, ...(next ? { weaponSize: undefined } : {}) });
            }}
            className={`${selectCls} text-[10px] ${d.specialType ? 'text-amber-400' : ''}`}
            title="Ability damage and negative levels are excluded from the HP total and listed separately"
          >
            <option value="">HP Damage</option>
            <optgroup label="Ability Damage">
              {(['str', 'dex', 'con', 'int', 'wis', 'cha'] as SpecialDamageType[]).map((t) => (
                <option key={t} value={t}>{SPECIAL_DAMAGE_LABELS[t]}</option>
              ))}
            </optgroup>
            <optgroup label="Other">
              <option value="negative_level">Negative Level</option>
            </optgroup>
          </select>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <label className="text-[10px] text-parchment-muted/40 whitespace-nowrap">Crit ×</label>
          <input type="number" value={d.critMult} min={1} max={10}
            onChange={(e) => onChange({ critMult: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)) })}
            className={inputCls + ' w-12 text-center'}
            title="1 = not multiplied on a critical hit"
          />
        </div>
        {/* Size scaling — disabled for special damage categories (ability damage / negative levels) */}
        <div className="flex items-center gap-1 shrink-0">
          <label
            className={`text-[10px] whitespace-nowrap ${d.specialType ? 'text-parchment-muted/20' : 'text-parchment-muted/40'}`}
            title={d.specialType ? 'Size scaling does not apply to ability damage or negative levels' : 'Enable size-based dice scaling (PF1/3.5). Pick the base weapon/creature size for this damage row.'}
          >
            Size
          </label>
          <select
            value={d.specialType ? '' : (d.weaponSize ?? '')}
            onChange={(e) => onChange({ weaponSize: (e.target.value as WeaponSize) || undefined })}
            disabled={!!d.specialType}
            className={selectCls + ' text-[10px]' + (d.specialType ? ' opacity-30 cursor-not-allowed' : '')}
            title={d.specialType ? 'Size scaling does not apply to ability damage or negative levels' : 'Base weapon/creature size — leave blank to skip size scaling'}
          >
            <option value="">—</option>
            {WEAPON_SIZES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        {canRemove && (
          <button onClick={onRemove} className="p-1 text-danger/50 hover:text-danger transition-colors shrink-0">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {/* Second row: Notes/Type — full width so there's room to type */}
      <div className="flex items-center gap-1">
        <label className="text-[10px] text-parchment-muted/40 whitespace-nowrap">Notes/Type</label>
        <input value={d.notes} onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Slashing, Fire, etc."
          className={inputCls + ' flex-1'}
          maxLength={60}
        />
      </div>
      {/* Modifier components */}
      <div className="pl-2">
        <ModComponentsList
          components={d.modComponents}
          onUpdate={updateMod}
          onAdd={addMod}
          onRemove={removeMod}
          gameSystem={gameSystem}
          emptyHint="No modifier — add a stat ref (e.g. STR Modifier) or a fixed bonus"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AttackBlock
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
function AttackBlock({ attack, onChange, onRemove, onDuplicate, canRemove, gameSystem }: {
  attack:       AttackNodeDraft;
  onChange:     (patch: Partial<AttackNodeDraft>) => void;
  onRemove:     () => void;
  onDuplicate?: () => void;
  canRemove:    boolean;
  gameSystem?:  RuleSystem;
}) {
  function updateDamage(idx: number, patch: Partial<DamageDraft>) {
    onChange({ damage: attack.damage.map((d, i) => i === idx ? { ...d, ...patch } : d) });
  }
  function addDamage()               { onChange({ damage: [...attack.damage, blankDamage()] }); }
  function removeDamage(idx: number) { onChange({ damage: attack.damage.filter((_, i) => i !== idx) }); }

  function updateOnCritExtra(idx: number, patch: Partial<DamageDraft>) {
    onChange({ onCritExtra: attack.onCritExtra.map((d, i) => i === idx ? { ...d, ...patch } : d) });
  }
  function addOnCritExtra()               { onChange({ onCritExtra: [...attack.onCritExtra, blankDamage()] }); }
  function removeOnCritExtra(idx: number) { onChange({ onCritExtra: attack.onCritExtra.filter((_, i) => i !== idx) }); }

  function updateAttackComp(idx: number, patch: Partial<ModCompDraft>) {
    onChange({ attackComponents: attack.attackComponents.map((c, i) => i === idx ? { ...c, ...patch } : c) });
  }
  function addAttackComp(type: 'stat_ref' | 'manual') {
    onChange({
      attackComponents: [
        ...attack.attackComponents,
        type === 'stat_ref' ? newStatRef(defaultStatKeyForSystem(gameSystem ?? 'pathfinder1')) : newManual(),
      ],
    });
  }
  function removeAttackComp(idx: number) {
    onChange({ attackComponents: attack.attackComponents.filter((_, i) => i !== idx) });
  }
  function toggleWeaponProperty(prop: WeaponSpecialProperty, checked: boolean) {
    const current = attack.weaponProperties ?? [];
    onChange({
      weaponProperties: checked
        ? [...new Set([...current, prop])]
        : current.filter((p) => p !== prop),
    });
  }

  // Header summary of attack components
  const compSummary = attack.attackComponents.length
    ? attack.attackComponents.map((c) =>
        c.type === 'stat_ref'
          ? (STAT_DEF_MAP[c.statKey]?.label ?? c.statKey)
          : c.value > 0 ? `+${c.value}` : String(c.value)
      ).join(' ')
    : '—';

  return (
    <div className="bg-darkbg-tertiary rounded-lg border border-darkbg-border/60 overflow-hidden">
      {/* Header — click to expand/collapse; label is a standard field inside the card */}
      <button
        type="button"
        onClick={() => onChange({ expanded: !attack.expanded })}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left
                   hover:bg-darkbg-secondary/40 transition-colors"
      >
        {attack.expanded
          ? <ChevronUp   className="w-3.5 h-3.5 text-parchment-muted/40 shrink-0" />
          : <ChevronDown className="w-3.5 h-3.5 text-parchment-muted/40 shrink-0" />}
        <span className="flex-1 font-display text-sm text-parchment truncate">
          {attack.label || <span className="text-parchment-muted/30 font-sans font-normal">Unnamed Attack</span>}
        </span>
        <span className="text-[10px] text-parchment-muted/40 truncate max-w-[140px] shrink-0">
          {compSummary}
        </span>
        {onDuplicate && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="p-1 text-parchment-muted/30 hover:text-gold transition-colors shrink-0"
            title="Duplicate this attack"
          >
            <Copy className="w-3.5 h-3.5" />
          </span>
        )}
        {canRemove && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-1 text-danger/40 hover:text-danger transition-colors shrink-0"
            title="Remove this attack"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {attack.expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-darkbg-border/40">
          {/* Attack name — standard labeled field, consistent with all other fields */}
          <div className="pt-2">
            <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Attack Name</label>
            <input value={attack.label} onChange={(e) => onChange({ label: e.target.value })}
              placeholder="e.g. Strike 1, Power Attack, Sneak Attack"
              className={inputCls} maxLength={60} />
          </div>
          {/* Attack parameters row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Attack Type</label>
              <select
                value={attack.attackKind ?? ''}
                onChange={(e) => onChange({ attackKind: e.target.value ? e.target.value as AttackKind : undefined })}
                className={selectCls + ' w-full'}
              >
                {ATTACK_KINDS.map((t) => <option key={t.value || 'general'} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Vs. Target</label>
              <select value={attack.vsTarget} onChange={(e) => onChange({ vsTarget: e.target.value })}
                className={selectCls + ' w-full'}>
                {VS_TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-parchment-muted/40 mb-0.5">
                Critical Range <span className="text-parchment-muted/25">(lower bound–20)</span>
              </label>
              <input type="number" value={attack.critMin} min={1} max={20}
                onChange={(e) => onChange({ critMin: Math.min(20, Math.max(1, parseInt(e.target.value) || 20)) })}
                className={inputCls}
                disabled={attack.noCrit}
              />
            </div>
          </div>

          {/* No-crit toggle */}
          <label className="flex items-center gap-2 cursor-pointer group w-fit">
            <input
              type="checkbox"
              checked={attack.noCrit}
              onChange={(e) => onChange({ noCrit: e.target.checked })}
              className="w-3.5 h-3.5 rounded border-darkbg-border accent-gold cursor-pointer"
            />
            <span className={`text-xs transition-colors ${attack.noCrit ? 'text-parchment/80' : 'text-parchment-muted/50'}`}>
              No critical hits
            </span>
            <span className="text-[10px] text-parchment-muted/30">
              (e.g. combat maneuver checks, grapple, trip)
            </span>
          </label>

          {/* D&D 3.5 weapon properties */}
          <div className={attack.noCrit ? 'opacity-30 pointer-events-none' : ''}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-parchment-muted/40">Weapon Properties</span>
              {(['vorpal', 'sharpness'] as WeaponSpecialProperty[]).map((prop) => (
                <label key={prop} className="flex items-center gap-1.5 cursor-pointer group w-fit">
                  <input
                    type="checkbox"
                    checked={(attack.weaponProperties ?? []).includes(prop)}
                    onChange={(e) => toggleWeaponProperty(prop, e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-darkbg-border accent-gold cursor-pointer"
                  />
                  <span className={`text-xs capitalize transition-colors ${(attack.weaponProperties ?? []).includes(prop) ? 'text-parchment/80' : 'text-parchment-muted/50'}`}>
                    {prop}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-parchment-muted/30 mt-1 leading-snug">
              Vorpal: natural 20 confirmed critical severs the head. Sharpness: natural 18+ confirmed critical severs a random non-head limb.
            </p>
          </div>

          {/* Perfect Roll — D&D 5e only */}
          {gameSystem === 'dnd5e' && !attack.noCrit && (
            <label className="flex items-center gap-2 cursor-pointer group w-fit">
              <input
                type="checkbox"
                checked={attack.critStyle === 'perfect'}
                onChange={(e) => onChange({ critStyle: e.target.checked ? 'perfect' : undefined })}
                className="w-3.5 h-3.5 rounded border-darkbg-border accent-gold cursor-pointer"
              />
              <span className={`text-xs transition-colors ${attack.critStyle === 'perfect' ? 'text-parchment/80' : 'text-parchment-muted/50'}`}>
                Perfect Rolls
              </span>
              <span className="text-[10px] text-parchment-muted/30">
                (crit: max normal dice + rolled crit dice — e.g. 2d8 [5, 3] → 16 + 8)
              </span>
            </label>
          )}

          {/* Attack modifier components */}
          <div>
            <label className="block text-[10px] text-parchment-muted/40 mb-1.5">
              Attack Bonus Components
              <span className="ml-1.5 text-parchment-muted/25 normal-case">
                — added to the d20 roll
              </span>
            </label>
            <ModComponentsList
              components={attack.attackComponents}
              onUpdate={updateAttackComp}
              onAdd={addAttackComp}
              onRemove={removeAttackComp}
              gameSystem={gameSystem}
              emptyHint="No modifiers — add stat refs (e.g. BAB Max, STR Modifier) or fixed bonuses"
            />
          </div>

          {/* On-hit damage rows */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-widest text-parchment-muted/40">Damage</span>
              <button onClick={addDamage}
                className="text-[10px] text-gold/60 hover:text-gold transition-colors flex items-center gap-0.5">
                <Plus className="w-3 h-3" /> Add Damage
              </button>
            </div>
            {attack.damage.length === 0 && (
              <p className="text-[10px] text-parchment-muted/30 italic py-1">
                No damage rows — this attack will not roll damage
                (e.g. combat maneuver checks, grapple, trip).
              </p>
            )}
            <div className="space-y-2">
              {attack.damage.map((d, i) => (
                <DamageRow key={d.id} d={d}
                  onChange={(p) => updateDamage(i, p)}
                  onRemove={() => removeDamage(i)}
                  canRemove={true}  // allow removing all rows — zero rows = no damage roll
                  gameSystem={gameSystem}
                />
              ))}
            </div>
            <p className="text-[10px] text-parchment-muted/30 mt-1.5 leading-snug">
              Set Crit × to 1 for damage applied on every hit but never multiplied on a crit
              (e.g. a flaming sword&#39;s 1d6 fire rider).
            </p>
          </div>

          {/* On-crit-extra damage — hidden when noCrit is enabled */}
          <div className={attack.noCrit ? 'opacity-30 pointer-events-none' : ''}>
            {attack.noCrit && (
              <p className="text-[10px] text-parchment-muted/30 italic mb-1">
                On Crit Extra is disabled — this attack cannot score a critical hit.
              </p>
            )}
            <div className="flex items-center justify-between mb-1.5">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-parchment-muted/40">On Crit Extra</span>
                <span className="ml-2 text-[10px] text-parchment-muted/25 normal-case">
                  rolled &amp; added only on a confirmed critical hit
                </span>
              </div>
              <button onClick={addOnCritExtra}
                className="text-[10px] text-gold/60 hover:text-gold transition-colors flex items-center gap-0.5">
                <Plus className="w-3 h-3" /> Add Crit Damage
              </button>
            </div>
            {attack.onCritExtra.length === 0 ? (
              <p className="text-[10px] text-parchment-muted/25 italic">
                None — add rows for damage only on a critical hit (e.g. flaming burst +1d10 fire).
              </p>
            ) : (
              <div className="space-y-2">
                {attack.onCritExtra.map((d, i) => (
                  // critMult is irrelevant for onCritExtra — always added once
                  <div key={d.id} className="bg-darkbg-secondary/30 rounded-lg border border-darkbg-border/50 p-2 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <div className="flex items-center gap-1 shrink-0">
                        <label className="text-[10px] text-parchment-muted/40 whitespace-nowrap">Damage</label>
                        <input value={d.diceExpr} onChange={(e) => updateOnCritExtra(i, { diceExpr: e.target.value })}
                          placeholder="e.g. 1d10"
                          className={(!isPureDiceExpr(d.diceExpr) && d.diceExpr !== '' ? inputErrCls : inputCls) + ' w-24'}
                          maxLength={10}
                        />
                      </div>
                      <div className="flex items-center gap-1 flex-1 min-w-[100px]">
                        <label className="text-[10px] text-parchment-muted/40 whitespace-nowrap">Notes/Type</label>
                        <input value={d.notes} onChange={(e) => updateOnCritExtra(i, { notes: e.target.value })}
                          placeholder="Fire"
                          className={inputCls} maxLength={60}
                        />
                      </div>
                      <button onClick={() => removeOnCritExtra(i)}
                        className="p-1 text-danger/50 hover:text-danger transition-colors shrink-0">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    {/* On-crit-extra can also have stat-ref modifiers (e.g. INT modifier for INT-based special damage) */}
                    <div className="pl-2">
                      <ModComponentsList
                        components={d.modComponents}
                        onUpdate={(idx, p) => updateOnCritExtra(i, {
                          modComponents: d.modComponents.map((c, j) => j === idx ? { ...c, ...p } : c),
                        })}
                        onAdd={(type) => updateOnCritExtra(i, {
                          modComponents: [...d.modComponents, type === 'stat_ref' ? newStatRef('str_mod') : newManual()],
                        })}
                        onRemove={(idx) => updateOnCritExtra(i, {
                          modComponents: d.modComponents.filter((_, j) => j !== idx),
                        })}
                        gameSystem={gameSystem}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main RoutineBuilder component
// ---------------------------------------------------------------------------
interface Props {
  initial?:           Partial<AttackRoutine>;
  /** Game system of the character owning this routine — used as default for new routines. */
  defaultGameSystem?: RuleSystem;
  saving:             boolean;
  onSave:             (data: { name: string; gameSystem: RuleSystem; definition: import('../../../../backend/shared/types/index').RoutineNode[] }) => void;
  onCancel:           () => void;
}

const SYSTEMS: { id: RuleSystem; label: string }[] = [
  { id: 'pathfinder1', label: 'Pathfinder 1e' },
  { id: 'dnd35',       label: 'D&D 3.5'       },
  { id: 'dnd5e',       label: 'D&D 5e'        },
];

export default function RoutineBuilder({ initial, defaultGameSystem, saving, onSave, onCancel }: Props) {
  const [name,       setName]       = useState(initial?.name ?? '');
  const [gameSystem, setGameSystem] = useState<RuleSystem>(initial?.gameSystem ?? defaultGameSystem ?? 'pathfinder1');
  const [attacks,    setAttacks]    = useState<AttackNodeDraft[]>(() => {
    if (initial?.definition?.length) {
      return initial.definition.filter((n) => n.type === 'attack').map((n) => {
        const a = n as AttackNode;
        // Migrate attackModSource / attackMod to attackComponents
        const attackComponents: ModCompDraft[] = a.attackComponents?.length
          ? a.attackComponents.map((c) => ({
              id: c.id, type: c.type,
              statKey: c.statKey
                ? normalizeStatKeyForSystem(c.statKey, gameSystem)
                : defaultStatKeyForSystem(gameSystem),
              value: c.value ?? 0,
              descriptor: c.descriptor ?? '',
            }))
          : (a as any).attackModSource?.type === 'stat_ref'
            ? [{ id: crypto.randomUUID(), type: 'stat_ref', statKey: (a as any).attackModSource.key, value: 0, descriptor: '' }]
            : [{ id: crypto.randomUUID(), type: 'manual', statKey: '', value: a.attackMod ?? 0, descriptor: '' }];

        return {
          id: a.id, label: a.label,
          attackKind: a.attackKind,
          attackComponents,
          vsTarget: a.vsTarget ?? 'AC',
          // critRange may be absent on routines saved before it was added — default to 20
          critMin: a.critRange?.[0] ?? 20,
          noCrit: !!(a as any).noCrit,
          critStyle: (a as any).critStyle ?? undefined,
          weaponProperties: (a as any).weaponProperties ?? [],
          damage: (a.damage ?? []).map(migrateDamage),
          onCritExtra: (a.onCritExtra ?? []).map(migrateDamage),
          expanded: false,
        };
      });
    }
    return [blankAttack()];
  });
  const [conditionals, setConditionals] = useState<ConditionalDamageDraft[]>(() => {
    if (initial?.definition?.length) {
      return (initial.definition as any[])
        .filter((n: any) => n.type === 'conditional_damage')
        .map((n: any) => ({
          id: n.id, label: n.label ?? '',
          subject: n.condition.subject, op: n.condition.op, value: n.condition.value,
          damage: n.damage?.map((dd: any) => migrateDamage(dd)) ?? [blankDamage()],
        }));
    }
    return [];
  });
  const [bonusAttacks, setBonusAttacks] = useState<BonusAttackNodeDraft[]>(() => {
    if (initial?.definition?.length) {
      return (initial.definition as any[])
        .filter((n: any) => n.type === 'bonus_attack')
        .map((n: any) => {
          const attackNode = n.attack as AttackNode;
          const migrated: AttackNodeDraft = {
            id: attackNode.id,
            label: attackNode.label,
            attackKind: attackNode.attackKind,
            attackComponents: attackNode.attackComponents?.map((c) => ({
              id: c.id, type: c.type,
              statKey: c.statKey
                ? normalizeStatKeyForSystem(c.statKey, gameSystem)
                : defaultStatKeyForSystem(gameSystem),
              value: c.value ?? 0,
              descriptor: c.descriptor ?? '',
            })) ?? [{ id: crypto.randomUUID(), type: 'manual', statKey: '', value: attackNode.attackMod ?? 0, descriptor: '' }],
            vsTarget: attackNode.vsTarget ?? 'AC',
            critMin: attackNode.critRange?.[0] ?? 20,
            noCrit: !!attackNode.noCrit,
            critStyle: (attackNode as any).critStyle ?? undefined,
            weaponProperties: (attackNode as any).weaponProperties ?? [],
            damage: (attackNode.damage ?? []).map(migrateDamage),
            onCritExtra: (attackNode.onCritExtra ?? []).map(migrateDamage),
            expanded: false,
          };
          return {
            id: n.id,
            condition: n.condition,
            attack: migrated,
            expanded: false,
          };
        });
    }
    return [];
  });
  const [error, setError] = useState<string | null>(null);

  function applyPreset(key: string) {
    const preset = PRESETS[key];
    if (!preset) return;
    setAttacks(preset.attacks.map((a) => ({
      ...a,
      id: crypto.randomUUID(),
      attackComponents: a.attackComponents.map((c) => ({ ...c, id: crypto.randomUUID() })),
      damage: a.damage.map((_) => ({ ...blankDamage(), id: crypto.randomUUID() })),
      onCritExtra: [],
      expanded: false,
    })));
  }

  function updateAttack(idx: number, patch: Partial<AttackNodeDraft>) {
    setAttacks((prev) => prev.map((a, i) => i === idx ? { ...a, ...patch } : a));
  }
  function addAttack()               { setAttacks((prev) => [...prev, blankAttack()]); }
  function removeAttack(idx: number) { setAttacks((prev) => prev.filter((_, i) => i !== idx)); }

  function handleSave() {
    setError(null);
    if (!name.trim())         { setError('Routine name is required'); return; }
    if (!attacks.length)      { setError('At least one attack is required'); return; }
    for (const a of attacks) {
      if (!a.label?.trim())   { setError('Each attack must have a label'); return; }
      // damage rows are optional — zero rows = pure check attack (no damage roll)
      for (const d of [...a.damage, ...a.onCritExtra]) {
        if (d.diceExpr && !isPureDiceExpr(d.diceExpr)) {
          setError(`"${a.label}": "${d.diceExpr}" is not valid — use dice notation (e.g. 2d6) or a plain number (e.g. 2)`);
          return;
        }
      }
    }
    for (const c of conditionals) {
      for (const d of c.damage) {
        if (d.diceExpr && !isPureDiceExpr(d.diceExpr)) {
          setError(`Conditional "${c.label || 'bonus'}": "${d.diceExpr}" is not valid — use dice notation (e.g. 2d6) or a plain number (e.g. 2)`);
          return;
        }
      }
    }
    for (const ba of bonusAttacks) {
      if (!ba.attack.label.trim()) {
        setError('Each bonus attack must have a name (edit "Attack Name" inside the card)');
        return;
      }
      for (const d of ba.attack.damage) {
        if (d.diceExpr && !isPureDiceExpr(d.diceExpr)) {
          setError(`Bonus Attack "${ba.attack.label}": "${d.diceExpr}" is not valid — use dice notation (e.g. 2d6) or a plain number (e.g. 2)`);
          return;
        }
      }
      for (const d of ba.attack.onCritExtra) {
        if (d.diceExpr && !isPureDiceExpr(d.diceExpr)) {
          setError(`Bonus Attack "${ba.attack.label}" crit extra: "${d.diceExpr}" is not valid — use dice notation (e.g. 2d6) or a plain number (e.g. 2)`);
          return;
        }
      }
    }
    onSave({
      name: name.trim(), gameSystem,
      definition: [
        ...attacks.map((a) => draftToNode(a, gameSystem)),
        ...conditionals.map((c) => conditionalToNode(c, gameSystem)),
        ...bonusAttacks.map((ba) => bonusAttackToNode(ba, gameSystem)),
      ],
    });
  }

  return (
    <div className="space-y-4">
      {/* Name + system */}
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <div>
          <label className="block text-xs text-parchment-muted/60 mb-1">Routine Name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Full Attack" maxLength={100}
            className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-parchment-muted/60 mb-1">System</label>
          <select value={gameSystem} onChange={(e) => setGameSystem(e.target.value as RuleSystem)}
            className={selectCls}>
            {SYSTEMS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Presets */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-parchment-muted/40 uppercase tracking-widest flex items-center gap-1">
          <Wand2 className="w-3 h-3" /> Preset
        </span>
        {Object.entries(PRESETS).map(([key, p]) => (
          <button key={key} onClick={() => applyPreset(key)}
            className="text-xs px-2.5 py-1 rounded border border-darkbg-border
                       text-parchment-muted/60 hover:border-gold/40 hover:text-parchment transition-colors">
            {p.label}
          </button>
        ))}
      </div>

      {/* Attack blocks */}
      <div className="space-y-2">
        {attacks.map((a, i) => (
          <AttackBlock key={a.id} attack={a} gameSystem={gameSystem}
            onChange={(p) => updateAttack(i, p)}
            onRemove={() => removeAttack(i)}
            onDuplicate={() => {
              const copy: AttackNodeDraft = {
                ...a,
                id:       crypto.randomUUID(),
                label:    `${a.label} (copy)`,
                attackKind: a.attackKind,
                expanded: true,
                damage:      a.damage.map((d)      => ({ ...d, id: crypto.randomUUID() })),
                onCritExtra: a.onCritExtra.map((d) => ({ ...d, id: crypto.randomUUID() })),
                attackComponents: a.attackComponents.map((c) => ({ ...c, id: crypto.randomUUID() })),
              };
              setAttacks((prev) => [
                ...prev.slice(0, i + 1),
                copy,
                ...prev.slice(i + 1),
              ]);
            }}
            canRemove={attacks.length > 1} />
        ))}
        <button onClick={addAttack}
          className="w-full flex items-center justify-center gap-1.5 py-2
                     border border-dashed border-darkbg-border rounded-lg
                     text-xs text-parchment-muted/40 hover:text-gold hover:border-gold/40 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Attack
        </button>
      </div>

      {/* ── Conditional Bonus Damage ── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-parchment-muted/40">
              Conditional Damage
            </span>
            <span className="ml-2 text-[10px] text-parchment-muted/25 normal-case">
              — extra damage when a condition is met (e.g. Sneak Attack, Flurry)
            </span>
          </div>
          <button onClick={() => setConditionals((p) => [...p, blankConditional()])}
            className="text-[10px] text-gold/60 hover:text-gold transition-colors flex items-center gap-0.5">
            <Plus className="w-3 h-3" /> Add Conditional Damage
          </button>
        </div>
        {conditionals.map((c, ci) => {

          return (
            <div key={c.id} className="bg-darkbg-tertiary rounded-lg border border-darkbg-border/60 p-3 space-y-2">
              {/* Condition header */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] text-parchment-muted/40 shrink-0">When</span>
                <select
                  value={c.subject}
                  onChange={(e) => {
                    const subject = e.target.value as 'hits' | 'crit_hits';
                    setConditionals((p) => p.map((x, i) => i === ci
                      ? { ...x, subject, op: 'gte', value: subject === 'crit_hits' ? 1 : x.value }
                      : x));
                  }}
                  className={selectCls}
                >
                  <option value="hits">Total Hits</option>
                  <option value="crit_hits">On Critical Hit</option>
                </select>
                {c.subject === 'hits' && (
                  <>
                    <select value={c.op} onChange={(e) => setConditionals((p) => p.map((x, i) => i === ci ? { ...x, op: e.target.value as 'gte'|'lte'|'eq' } : x))}
                      className={selectCls}>
                      <option value="gte">≥</option>
                      <option value="lte">≤</option>
                      <option value="eq">=</option>
                    </select>
                    <input type="number" value={c.value} min={0}
                      onChange={(e) => setConditionals((p) => p.map((x, i) => i === ci ? { ...x, value: parseInt(e.target.value) || 0 } : x))}
                      className={inputCls + ' w-14 text-center'} />
                  </>
                )}
                <input value={c.label} onChange={(e) => setConditionals((p) => p.map((x, i) => i === ci ? { ...x, label: e.target.value } : x))}
                  placeholder='Label (e.g. "Sneak Attack")'
                  className={inputCls + ' flex-1 min-w-[140px]'} maxLength={60} />
                <button onClick={() => setConditionals((p) => p.filter((_, i) => i !== ci))}
                  className="p-1 text-danger/50 hover:text-danger transition-colors shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {/* Damage rows */}
              <div className="space-y-1.5 pl-2">
                <span className="text-[9px] text-parchment-muted/30 uppercase tracking-widest">
                  Bonus Damage
                </span>
                {c.damage.map((d, di) => (
                  <DamageRow key={d.id} d={d}
                    onChange={(patch) => setConditionals((p) => p.map((x, i) => i === ci ? {
                      ...x, damage: x.damage.map((dd, j) => j === di ? { ...dd, ...patch } : dd),
                    } : x))}
                    onRemove={() => setConditionals((p) => p.map((x, i) => i === ci ? {
                      ...x, damage: x.damage.filter((_, j) => j !== di),
                    } : x))}
                    canRemove={c.damage.length > 1}
                    gameSystem={gameSystem}
                  />
                ))}
                <button onClick={() => setConditionals((p) => p.map((x, i) => i === ci ? { ...x, damage: [...x.damage, blankDamage()] } : x))}
                  className="text-[10px] text-gold/60 hover:text-gold transition-colors flex items-center gap-0.5 pt-0.5">
                  <Plus className="w-3 h-3" /> Add Bonus Damage
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Bonus Attacks (Phase 5) ── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-parchment-muted/40">
              Bonus Attacks
            </span>
            <span className="ml-2 text-[10px] text-parchment-muted/25 normal-case">
              — conditional full attacks triggered by hit conditions
            </span>
          </div>
          <button onClick={() => setBonusAttacks((p) => [...p, blankBonusAttack()])}
            className="text-[10px] text-gold/60 hover:text-gold transition-colors flex items-center gap-0.5">
            <Plus className="w-3 h-3" /> Add Bonus Attack
          </button>
        </div>
        {bonusAttacks.map((ba, bai) => (
          <div key={ba.id} className="rounded-lg border border-gold/20 overflow-hidden">
            {/* Condition row — trigger condition above the attack card */}
            <div className="flex items-center gap-2 px-3 py-2 bg-darkbg-tertiary border-b border-gold/10">
              <span className="text-[10px] text-gold/50 uppercase tracking-widest shrink-0">Trigger</span>
              <select
                value={ba.condition.subject}
                onChange={(e) => {
                  const subject = e.target.value as 'hits' | 'crit_hits';
                  setBonusAttacks((p) => p.map((x, i) => i === bai
                    ? { ...x, condition: { subject, op: 'gte', value: subject === 'crit_hits' ? 1 : x.condition.value } }
                    : x));
                }}
                className={`px-2 py-1 text-xs bg-darkbg border border-darkbg-border rounded text-parchment focus:outline-none focus:border-gold/40`}
              >
                <option value="hits">Total Hits ≥</option>
                <option value="crit_hits">On Each Critical Hit</option>
              </select>
              {ba.condition.subject === 'hits' && (
                <input type="number" min={1} max={20} value={ba.condition.value}
                  onChange={(e) => setBonusAttacks((p) => p.map((x, i) => i === bai ? { ...x, condition: { ...x.condition, value: parseInt(e.target.value) || 1 } } : x))}
                  className="w-12 px-2 py-1 text-xs bg-darkbg border border-darkbg-border rounded text-parchment focus:outline-none focus:border-gold/40" />
              )}
            </div>

            {/* Attack card — label field inside is the only name */}
            <AttackBlock attack={ba.attack} gameSystem={gameSystem}
              onChange={(p) => setBonusAttacks((prev) => prev.map((x, i) => i === bai ? { ...x, attack: { ...x.attack, ...p } } : x))}
              onRemove={() => setBonusAttacks((p) => p.filter((_, i) => i !== bai))}
              canRemove={true}
            />
          </div>
        ))}
      </div>

      {/* ── Script Editor ── */}
      <ScriptEditor
        name={name}
        gameSystem={gameSystem}
        definition={[
          ...attacks.map((a) => draftToNode(a, gameSystem)),
          ...conditionals.map((c) => conditionalToNode(c, gameSystem)),
          ...bonusAttacks.map((ba) => bonusAttackToNode(ba, gameSystem)),
        ]}
        onScriptChange={(script: string) => {
          // Parse script and sync back to GUI blocks
          const result = parseRSLScript(script);

          if (!result.success) {
            setError(`RSL Parse Error: ${result.errors.join('; ')}`);
            return;
          }

          // Separate parsed nodes into attacks, conditionals, and bonus attacks
          const parsedAttacks: AttackNodeDraft[] = [];
          const parsedConditionals: ConditionalDamageDraft[] = [];
          const parsedBonusAttacks: BonusAttackNodeDraft[] = [];

          for (const node of result.nodes) {
            if (node.type === 'attack') {
              parsedAttacks.push(nodeToDraft(node as AttackNode));
            } else if (node.type === 'conditional_damage') {
              parsedConditionals.push(nodeToConditional(node as any));
            } else if (node.type === 'bonus_attack') {
              parsedBonusAttacks.push(nodeToBonus(node as BonusAttackNode));
            }
          }

          setAttacks(parsedAttacks);
          setConditionals(parsedConditionals);
          setBonusAttacks(parsedBonusAttacks);
          setError('');
        }}
      />

      {error && <p className="text-danger text-xs">{error}</p>}

      <div className="flex items-center gap-2 justify-end pt-1">
        <button onClick={onCancel} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-parchment-muted/60
                     hover:text-parchment border border-darkbg-border rounded transition-colors disabled:opacity-50">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs btn-primary disabled:opacity-50">
          {saving ? '…' : <><Check className="w-3.5 h-3.5" /> Save</>}
        </button>
      </div>
    </div>
  );
}
